import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

/* Tipos para la configuración de mantenimiento */
export interface MaintenanceConfig {
  isEnabled: boolean;
  title: string;
  message: string;
  estimatedTime: string;
  contactEmail: string;
  contactPhone: string;
  showProgress: boolean;
  showContactInfo: boolean;
  startTime?: Date;
  endTime?: Date;
  allowedRoles?: string[];
  allowedUsers?: string[];
  maintenanceType: 'scheduled' | 'emergency' | 'update';
}

/* Estado del contexto */
interface MaintenanceContextState {
  config: MaintenanceConfig;
  isMaintenanceActive: boolean;
  updateConfig: (config: Partial<MaintenanceConfig>) => void;
  enableMaintenance: (config?: Partial<MaintenanceConfig>) => void;
  disableMaintenance: () => void;
  checkMaintenanceStatus: () => Promise<void>;
}

/* Configuración por defecto */
const defaultConfig: MaintenanceConfig = {
  isEnabled: false,
  title: "Sistema en Mantenimiento Programado",
  message: "Estamos realizando mejoras importantes en nuestro sistema para ofrecerte una mejor experiencia. Durante este tiempo, el Portal de Acceso Digital no estará disponible.",
  estimatedTime: "2 horas aproximadamente",
  contactEmail: "contacto@poderjudicial.gob.do",
  contactPhone: "+1 (809) 200-3191",
  showProgress: true,
  showContactInfo: true,
  maintenanceType: 'scheduled',
  allowedRoles: ['admin', 'super_admin'],
  allowedUsers: []
};


const MaintenanceContext = createContext<MaintenanceContextState | undefined>(undefined);

interface MaintenanceProviderProps {
  children: ReactNode;
  checkInterval?: number; // en milisegundos, por defecto 5 minutos
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ 
  children, 
  checkInterval = 5 * 60 * 1000 // 5 minutos por defecto
}) => {
  const [config, setConfig] = useState<MaintenanceConfig>(() => {
    // Intentar cargar configuración desde localStorage
    try {
      const savedConfig = localStorage.getItem('maintenanceConfig');
      if (savedConfig) {
        const parsed = JSON.parse(savedConfig);
        return { ...defaultConfig, ...parsed };
      }
    } catch (error) {
      console.error('Error cargando información desde localStorage:', error);
    }
    return defaultConfig;
  });

  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);

  /* Verificar si el mantenimiento está activo */
  const checkIfMaintenanceIsActive = (currentConfig: MaintenanceConfig): boolean => {
    if (!currentConfig.isEnabled) return false;

    //TODO: Este valor debe ser obtenido desde uno de los API
    const now = new Date();
    
    /* Si hay horario programado, verificar */
    if (currentConfig.startTime && currentConfig.endTime) {
      const start = new Date(currentConfig.startTime);
      const end = new Date(currentConfig.endTime);
      return now >= start && now <= end;
    }

    /* Si no hay horario específico, el mantenimiento está activo si está habilitado */
    return true;
  };

  /** 
   * Método para Actualizar la configuración 
   */
  const updateConfig = (newConfig: Partial<MaintenanceConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    
    /* Guardar en localStorage */
    try {
      localStorage.setItem('maintenanceConfig', JSON.stringify(updatedConfig));
    } catch (error) {
    console.error('Error al guardar la configuración de mantenimiento en localStorage:', error);
    }
  };

  /** Habilitar mantenimiento */
  const enableMaintenance = (newConfig?: Partial<MaintenanceConfig>) => {
    const maintenanceConfig = {
      ...config,
      ...newConfig,
      isEnabled: true
    };
    updateConfig(maintenanceConfig);
  };

  /** Deshabilitar mantenimiento */
  const disableMaintenance = () => {
    updateConfig({ isEnabled: false });
  };

  /** Verificar estado desde API/servidor */ 
  const checkMaintenanceStatus = async () => {
    try {
      //TODO: implementar llamada al API - ver con anderson si API o ENV
      const envMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
      const envMaintenanceMessage = import.meta.env.VITE_MAINTENANCE_MESSAGE;
      const envMaintenanceTime = import.meta.env.VITE_MAINTENANCE_TIME;
      const envMaintenanceBypass = import.meta.env.VITE_MAINTENANCE_BYPASS === 'true';

      if (envMaintenanceMode) {
        updateConfig({
          isEnabled: envMaintenanceBypass === false,
          message: envMaintenanceMessage || config.message,
          estimatedTime: envMaintenanceTime || config.estimatedTime,
          maintenanceType: 'scheduled'
        });
      }

    /* llamada a API */      
    //   const response = await fetch('/api/maintenance/status');
    //   const data = await response.json();
      
    //   if (data.isMaintenanceMode) {
    //     updateConfig({
    //       isEnabled: true,
    //       ...data.config
    //     });
    //   } else {
    //     updateConfig({ isEnabled: false });
    //   }
      
    } catch (error) {
      console.error('Error checking maintenance status:', error);
    }
  };

  // Efecto para verificar el estado de mantenimiento
  useEffect(() => {
    const active = checkIfMaintenanceIsActive(config);
    setIsMaintenanceActive(active);
  }, [config]);

  // Efecto para verificación periódica
  useEffect(() => {
    // Verificación inicial
    checkMaintenanceStatus();

    // Configurar verificación periódica
    const interval = setInterval(checkMaintenanceStatus, checkInterval);

    return () => clearInterval(interval);
  }, [checkInterval]);

  // Verificar cambios de tiempo para mantenimientos programados
  useEffect(() => {
    if (config.startTime && config.endTime) {
      //TODO: Cambiar a uso de fecha del servidor
      const now = new Date().getTime();
      const startTime = new Date(config.startTime).getTime();
      const endTime = new Date(config.endTime).getTime();

      // Programar inicio del mantenimiento
      if (startTime > now) {
        const timeoutStart = setTimeout(() => {
          setIsMaintenanceActive(true);
        }, startTime - now);

        // Programar fin del mantenimiento
        if (endTime > now) {
          const timeoutEnd = setTimeout(() => {
            setIsMaintenanceActive(false);
            updateConfig({ isEnabled: false });
          }, endTime - now);

          return () => {
            clearTimeout(timeoutStart);
            clearTimeout(timeoutEnd);
          };
        }

        return () => clearTimeout(timeoutStart);
      }
    }
  }, [config.startTime, config.endTime]);

  const contextValue: MaintenanceContextState = {
    config,
    isMaintenanceActive,
    updateConfig,
    enableMaintenance,
    disableMaintenance,
    checkMaintenanceStatus
  };

  return (
    <MaintenanceContext.Provider value={contextValue}>
      {children}
    </MaintenanceContext.Provider>
  );
};

/* Hook personalizado para usar el contexto */
export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error('useMaintenanceMode debe utilizarse dentro de (un) MaintenanceProvider');
  }
  return context;
};
